import React, { useState } from 'react';
import { billingAPI, payrollAPI, reportsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ChartLine, Download, CurrencyDollar, Users, Receipt, Scales, WarningCircle, CheckCircle, CaretDown, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, endOfWeek } from 'date-fns';

const moneyFmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const downloadCsv = (filename, rows) => {
  if (!rows || !rows.length) {
    toast.error('No data to export');
    return;
  }
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
};

export default function ReportsPage() {
  const today = new Date();
  const [start, setStart] = useState(format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [end, setEnd] = useState(format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));

  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(null);
  const [payroll, setPayroll] = useState(null);
  const [margin, setMargin] = useState(null);
  const [crossCheck, setCrossCheck] = useState(null);
  const [expandedCg, setExpandedCg] = useState(null);

  const runAll = async () => {
    setLoading(true);
    try {
      const params = { start_date: start, end_date: end };
      const [bRes, pRes, mRes, xRes] = await Promise.all([
        billingAPI.getAgencyBilling(params),
        payrollAPI.getAgencyPayroll(params),
        reportsAPI.grossMargin(params),
        reportsAPI.billingPayrollCrossCheck(params),
      ]);
      setBilling(bRes.data);
      setPayroll(pRes.data);
      setMargin(mRes.data);
      setCrossCheck(xRes.data);
      setExpandedCg(null);
      toast.success('Reports refreshed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reports');
    } finally { setLoading(false); }
  };

  React.useEffect(() => { runAll(); /* eslint-disable-next-line */ }, []);

  const quickRange = (days) => {
    setStart(format(subDays(today, days), 'yyyy-MM-dd'));
    setEnd(format(today, 'yyyy-MM-dd'));
  };

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold flex items-center gap-3">
            <ChartLine weight="duotone" className="w-8 h-8 text-primary" /> Reports
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Pull client billing, caregiver payroll, and gross-margin reports for any date range.</p>
        </div>
      </div>

      {/* Date Range */}
      <Card className="rounded-2xl border border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Start date</Label>
              <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="rounded-xl" data-testid="reports-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End date</Label>
              <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="rounded-xl" data-testid="reports-end-date" />
            </div>
            <div className="flex gap-1 flex-wrap">
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => quickRange(7)} data-testid="quick-week">This week</Button>
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => quickRange(14)} data-testid="quick-2weeks">Last 2 wks</Button>
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => quickRange(30)} data-testid="quick-month">Last 30 days</Button>
            </div>
            <Button onClick={runAll} disabled={loading} className="rounded-xl" data-testid="run-reports-btn">
              {loading ? 'Running...' : 'Run Reports'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="billing" className="space-y-4">
        <TabsList className="rounded-xl">
          <TabsTrigger value="billing" className="rounded-lg" data-testid="tab-client-billing">
            <Receipt weight="duotone" className="w-4 h-4 mr-1" /> Client Billing
          </TabsTrigger>
          <TabsTrigger value="payroll" className="rounded-lg" data-testid="tab-caregiver-payroll">
            <Users weight="duotone" className="w-4 h-4 mr-1" /> Caregiver Payroll
          </TabsTrigger>
          <TabsTrigger value="margin" className="rounded-lg" data-testid="tab-gross-margin">
            <CurrencyDollar weight="duotone" className="w-4 h-4 mr-1" /> Gross Margin
          </TabsTrigger>
          <TabsTrigger value="crosscheck" className="rounded-lg" data-testid="tab-cross-check">
            <Scales weight="duotone" className="w-4 h-4 mr-1" /> Cross-Check
            {crossCheck?.caregivers_flagged > 0 && (
              <Badge className="ml-1.5 bg-red-100 text-red-800 rounded-full text-[10px] px-1.5">{crossCheck.caregivers_flagged}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Client Billing */}
        <TabsContent value="billing">
          <Card className="rounded-2xl border border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Client Billing Report</CardTitle>
                  <CardDescription>All billed revenue by client for {start} → {end}</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => downloadCsv(`client-billing_${start}_to_${end}.csv`, billing?.client_breakdown || [])} data-testid="export-billing-csv">
                  <Download weight="bold" className="w-3.5 h-3.5" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {billing ? (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <StatBox label="Total Hours" value={(billing.total_hours || 0).toFixed(2)} />
                    <StatBox label="Total Visits" value={billing.total_visits || 0} />
                    <StatBox label="Total Billed" value={moneyFmt(billing.total_amount)} highlight />
                  </div>
                  <div className="border border-border rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Client</TableHead>
                          <TableHead className="text-right">Visits</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Total Billed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(billing.client_breakdown || []).length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No billable visits in this range</TableCell></TableRow>
                        ) : billing.client_breakdown.map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{c.client_name}</TableCell>
                            <TableCell className="text-right">{c.visit_count}</TableCell>
                            <TableCell className="text-right">{(c.total_hours || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right font-medium">{moneyFmt(c.total_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">Run the reports to view billing.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Caregiver Payroll */}
        <TabsContent value="payroll">
          <Card className="rounded-2xl border border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Caregiver Payroll Report</CardTitle>
                  <CardDescription>All caregiver pay for {start} → {end} (40 hr/week FLSA overtime applied)</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => downloadCsv(`caregiver-payroll_${start}_to_${end}.csv`, payroll?.caregiver_breakdown || [])} data-testid="export-payroll-csv">
                  <Download weight="bold" className="w-3.5 h-3.5" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {payroll ? (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <StatBox label="Total Hours" value={(payroll.total_hours || 0).toFixed(2)} />
                    <StatBox label="Caregivers" value={(payroll.caregiver_breakdown || []).length} />
                    <StatBox label="Total Pay" value={moneyFmt(payroll.total_pay)} highlight />
                  </div>
                  <div className="border border-border rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Caregiver</TableHead>
                          <TableHead className="text-right">Visits</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Reg</TableHead>
                          <TableHead className="text-right">OT</TableHead>
                          <TableHead className="text-right">Total Pay</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(payroll.caregiver_breakdown || []).length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No caregiver visits in this range</TableCell></TableRow>
                        ) : payroll.caregiver_breakdown.map((cg, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{cg.caregiver_name}</TableCell>
                            <TableCell className="text-right">{cg.visit_count}</TableCell>
                            <TableCell className="text-right">{(cg.total_hours || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{(cg.regular_hours || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right">
                              {cg.overtime_hours > 0
                                ? <Badge className="bg-orange-100 text-orange-800 rounded-full text-xs">{cg.overtime_hours.toFixed(2)}</Badge>
                                : <span className="text-muted-foreground">0.00</span>}
                            </TableCell>
                            <TableCell className="text-right font-medium">{moneyFmt(cg.total_pay)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">Run the reports to view payroll.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gross Margin */}
        <TabsContent value="margin">
          <Card className="rounded-2xl border border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Gross Margin Report</CardTitle>
                  <CardDescription>Revenue minus caregiver cost per client, for {start} → {end}</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => downloadCsv(`gross-margin_${start}_to_${end}.csv`, margin?.clients || [])} data-testid="export-margin-csv">
                  <Download weight="bold" className="w-3.5 h-3.5" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {margin ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <StatBox label="Revenue" value={moneyFmt(margin.total_revenue)} />
                    <StatBox label="Caregiver Cost" value={moneyFmt(margin.total_cost)} />
                    <StatBox label="Gross Margin" value={moneyFmt(margin.total_gross_margin)} highlight />
                    <StatBox label="Margin %" value={`${margin.total_gross_margin_pct}%`} highlight />
                  </div>
                  <div className="border border-border rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Client</TableHead>
                          <TableHead className="text-right">Visits</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Gross Margin</TableHead>
                          <TableHead className="text-right">Margin %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(margin.clients || []).length === 0 ? (
                          <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No data in this range</TableCell></TableRow>
                        ) : margin.clients.map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{c.client_name}</TableCell>
                            <TableCell className="text-right">{c.visit_count}</TableCell>
                            <TableCell className="text-right">{c.total_hours.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{moneyFmt(c.revenue)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{moneyFmt(c.caregiver_cost)}</TableCell>
                            <TableCell className={`text-right font-medium ${c.gross_margin < 0 ? 'text-red-600' : ''}`}>{moneyFmt(c.gross_margin)}</TableCell>
                            <TableCell className="text-right">
                              <Badge className={`rounded-full text-xs ${c.gross_margin_pct < 20 ? 'bg-orange-100 text-orange-800' : c.gross_margin_pct < 40 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                                {c.gross_margin_pct}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">Run the reports to view gross margin.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing vs Payroll Cross-Check */}
        <TabsContent value="crosscheck">
          <Card className="rounded-2xl border border-border" data-testid="cross-check-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Billing vs Payroll Cross-Check</CardTitle>
                  <CardDescription>Side-by-side hours per caregiver for {start} → {end} — mismatches flagged automatically. Click a row for shift details.</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => downloadCsv(`billing-payroll-crosscheck_${start}_to_${end}.csv`, (crossCheck?.caregivers || []).map(({ visits, flags, ...r }) => ({ ...r, flags: flags.join('; ') })))} data-testid="export-crosscheck-csv">
                  <Download weight="bold" className="w-3.5 h-3.5" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {crossCheck ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <StatBox label="Billed Hours" value={(crossCheck.total_billed_hours || 0).toFixed(2)} />
                    <StatBox label="Payroll Hours" value={(crossCheck.total_payroll_hours || 0).toFixed(2)} />
                    <StatBox label="Hours Variance" value={(crossCheck.total_hours_variance || 0).toFixed(2)} highlight={Math.abs(crossCheck.total_hours_variance) > 0.01} />
                    <StatBox label="Caregivers Flagged" value={crossCheck.caregivers_flagged || 0} highlight={crossCheck.caregivers_flagged > 0} />
                  </div>
                  <div className="border border-border rounded-xl overflow-hidden">
                    <Table data-testid="cross-check-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Caregiver</TableHead>
                          <TableHead className="text-right">Visits</TableHead>
                          <TableHead className="text-right">Billed Hrs</TableHead>
                          <TableHead className="text-right">Payroll Hrs</TableHead>
                          <TableHead className="text-right">Variance</TableHead>
                          <TableHead className="text-right">Billed $</TableHead>
                          <TableHead className="text-right">Payroll $</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(crossCheck.caregivers || []).length === 0 ? (
                          <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">No visits in this range</TableCell></TableRow>
                        ) : crossCheck.caregivers.map((cg, i) => (
                          <React.Fragment key={cg.caregiver_id}>
                            <TableRow
                              className={`cursor-pointer hover:bg-muted/40 ${cg.mismatch ? 'bg-red-50/60' : ''}`}
                              onClick={() => setExpandedCg(expandedCg === cg.caregiver_id ? null : cg.caregiver_id)}
                              data-testid={`cross-check-row-${i}`}
                            >
                              <TableCell className="pr-0">
                                {expandedCg === cg.caregiver_id ? <CaretDown className="w-3.5 h-3.5" /> : <CaretRight className="w-3.5 h-3.5" />}
                              </TableCell>
                              <TableCell className="font-medium">{cg.caregiver_name}</TableCell>
                              <TableCell className="text-right">{cg.visit_count}</TableCell>
                              <TableCell className="text-right">{cg.billed_hours.toFixed(2)}</TableCell>
                              <TableCell className="text-right">{cg.payroll_hours.toFixed(2)}</TableCell>
                              <TableCell className={`text-right font-medium ${Math.abs(cg.hours_variance) > 0.01 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                {cg.hours_variance > 0 ? '+' : ''}{cg.hours_variance.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right">{moneyFmt(cg.billed_amount)}</TableCell>
                              <TableCell className="text-right text-muted-foreground">{moneyFmt(cg.payroll_amount)}</TableCell>
                              <TableCell>
                                {cg.mismatch ? (
                                  <div className="space-y-1">
                                    <Badge className="bg-red-100 text-red-800 rounded-full text-xs gap-1" data-testid={`cross-check-mismatch-${i}`}>
                                      <WarningCircle weight="bold" className="w-3 h-3" /> Mismatch
                                    </Badge>
                                    <div className="flex flex-wrap gap-1">
                                      {cg.flags.map(f => (
                                        <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">{FLAG_LABELS[f] || f}</span>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-800 rounded-full text-xs gap-1">
                                    <CheckCircle weight="bold" className="w-3 h-3" /> Match
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                            {expandedCg === cg.caregiver_id && (
                              <TableRow className="bg-muted/20 hover:bg-muted/20">
                                <TableCell colSpan={9} className="p-0">
                                  <div className="px-6 py-3" data-testid={`cross-check-detail-${i}`}>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="text-xs">Date</TableHead>
                                          <TableHead className="text-xs">Client</TableHead>
                                          <TableHead className="text-xs text-right">Billed Hrs</TableHead>
                                          <TableHead className="text-xs text-right">Payroll Hrs</TableHead>
                                          <TableHead className="text-xs text-right">Billed $</TableHead>
                                          <TableHead className="text-xs text-right">Payroll $</TableHead>
                                          <TableHead className="text-xs">Flags</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {cg.visits.map((vv, j) => (
                                          <TableRow key={vv.visit_id + j} className={vv.flags.length ? 'bg-red-50/50' : ''}>
                                            <TableCell className="text-xs whitespace-nowrap">{vv.date}</TableCell>
                                            <TableCell className="text-xs">{vv.client_name}</TableCell>
                                            <TableCell className="text-xs text-right">{vv.hours.toFixed(2)}</TableCell>
                                            <TableCell className="text-xs text-right">{vv.payroll_hours.toFixed(2)}</TableCell>
                                            <TableCell className="text-xs text-right">{moneyFmt(vv.billed_amount)}</TableCell>
                                            <TableCell className="text-xs text-right">{moneyFmt(vv.payroll_amount)}</TableCell>
                                            <TableCell className="text-xs">
                                              {vv.flags.length ? vv.flags.map(f => (
                                                <span key={f} className="text-[10px] mr-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">{FLAG_LABELS[f] || f}</span>
                                              )) : <span className="text-muted-foreground">—</span>}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">Run the reports to view the cross-check.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const FLAG_LABELS = {
  no_caregiver_profile: 'No caregiver profile linked',
  no_clock_times: 'No clock in/out (scheduled hrs used)',
  missing_billing_rate: 'Client missing billing rate',
  missing_pay_rate: 'Caregiver missing pay rate',
};

const StatBox = ({ label, value, highlight }) => (
  <div className={`rounded-xl p-3 border ${highlight ? 'bg-primary/5 border-primary/30' : 'bg-muted/40 border-border'}`}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-heading text-xl font-semibold mt-0.5">{value}</p>
  </div>
);
